import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import {
  type FilterDefinition,
  useFiltersDefinition,
} from "@/pages/(app)/returns/hooks/use-filter-definitions";
import { ShopViewDto } from "@babackapp/private-api-client/types";
import { SortingState } from "@tanstack/react-table";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from "react";
import {
  isDateFilterName,
  isStringFilterName,
} from "../list/utils/check-filter-type";
import {
  DateFilterCondition,
  DateFilterName,
  FilterName,
  StringFilterCondition,
  StringFilterName,
} from "../list/utils/types";

export type DateFrontFilterValueType =
  | "1d"
  | "3d"
  | "1w"
  | "2w"
  | "3w"
  | "1m"
  | "2m"
  | "3m"
  | "6m";
export type StringFrontFilterValue = {
  condition: StringFilterCondition;
  value: string[];
};
export type DateFrontFilterValue = StringFrontFilterValue;
type FilterMap = Map<FilterName, StringFrontFilterValue>;

type AddFilterPayload = {
  isMulti: boolean;
} & (
  | { name: StringFilterName; value: string; condition: StringFilterCondition }
  | {
      name: DateFilterName;
      value: string[];
      condition: DateFilterCondition;
    }
);

type RemoveFilterPayload = {
  isMulti: boolean;
} & (
  | { name: StringFilterName; value: string; condition: StringFilterCondition }
  | {
      name: DateFilterName;
      value: string[];
      condition: DateFilterCondition;
    }
);

type ClearFilterPayload = {
  name: FilterName;
};

type UpdateFilterConditionPayload =
  | { name: StringFilterName; condition: StringFilterCondition }
  | { name: DateFilterName; condition: DateFilterCondition };

type UpdateSearchPayload = {
  search?: string;
};

enum ActionTypes {
  addFilter = "ADD_FILTER",
  removeFilter = "REMOVE_FILTER",
  clearFilter = "CLEAR_FILTER",
  clearAll = "CLEAR_ALL",
  updateFilterCondition = "UPDATE_FILTER_CONDITION",
  updateSearch = "UPDATE_SEARCH",
  clearSearch = "CLEAR_SEARCH",
}

interface IAction<T, P> {
  type: T;
  payload: P;
}

type QuickFilter =
  | "toBeValidated"
  | "toBeTreated"
  | "shippingTooLong"
  | "carrierError"
  | "carrierIssues"
  | "marketplace";

type Action =
  | IAction<ActionTypes.addFilter, AddFilterPayload>
  | IAction<ActionTypes.removeFilter, RemoveFilterPayload>
  | IAction<ActionTypes.clearFilter, ClearFilterPayload>
  | IAction<ActionTypes.clearAll, undefined>
  | IAction<ActionTypes.updateFilterCondition, UpdateFilterConditionPayload>
  | IAction<ActionTypes.updateSearch, UpdateSearchPayload>
  | IAction<ActionTypes.clearSearch, undefined>;

type ReturnRequestsFiltersContextValue = {
  filters: FilterMap;

  quickFilter?: QuickFilter | null;
  setQuickFilter: (quickFilter: QuickFilter | null) => void;

  view: ShopViewDto | null;
  setView: (view: ShopViewDto | null) => void;
  search?: string;
  sorting: SortingState;
  setSorting: (sorting: SortingState) => void;
  filtersDefinition: FilterDefinition;
  addFilter: (payload: AddFilterPayload) => void;
  removeFilter: (payload: RemoveFilterPayload) => void;
  clearFilter: (payload: ClearFilterPayload) => void;
  isAnyFilterApplied: boolean;
  clearAll: () => void;
  updateFilterCondition: (payload: UpdateFilterConditionPayload) => void;
  updateSearch: (payload: UpdateSearchPayload) => void;
  clearSearch: () => void;
  page: number;
  setPage: (p: number) => void;
};

interface State
  extends Pick<ReturnRequestsFiltersContextValue, "filters" | "search"> {}

export const ReturnRequestsFiltersContext = createContext<
  ReturnRequestsFiltersContextValue | undefined
>(undefined);

function filtersReducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionTypes.addFilter: {
      const filters = new Map(state.filters);
      const filter = filters.get(action.payload.name);

      if (filter) {
        if (isStringFilterName(action.payload.name)) {
          filters.set(action.payload.name, {
            ...filter,
            value: action.payload.isMulti
              ? [...filter.value, action.payload.value]
              : action.payload.value,
            condition: action.payload.condition,
          } as StringFrontFilterValue);
        } else {
          filters.set(action.payload.name, {
            ...filter,
            value: action.payload.value,
            condition: action.payload.condition,
          } as DateFrontFilterValue);
        }
      } else {
        if (isStringFilterName(action.payload.name)) {
          filters.set(action.payload.name, {
            name: action.payload.name,
            value: action.payload.isMulti
              ? [action.payload.value]
              : action.payload.value,
            condition: action.payload.condition,
          } as StringFrontFilterValue);
        } else {
          filters.set(action.payload.name, {
            name: action.payload.name,
            value: action.payload.value,
            condition: action.payload.condition,
          } as DateFrontFilterValue);
        }
      }

      return {
        ...state,
        filters,
      };
    }
    case ActionTypes.removeFilter: {
      const filters = new Map(state.filters);
      const filter = filters.get(action.payload.name);

      if (filter && isStringFilterName(action.payload.name)) {
        const value = (filter as StringFrontFilterValue).value.filter(
          (value) => value !== action.payload.value,
        );

        if (value.length) {
          filters.set(action.payload.name, {
            ...filter,
            value,
          } as StringFrontFilterValue);
        } else {
          filters.delete(action.payload.name);
        }
      }
      return {
        ...state,
        filters,
      };
    }
    case ActionTypes.clearFilter: {
      const filters = new Map(state.filters);
      filters.delete(action.payload.name);

      return {
        ...state,
        filters,
      };
    }
    case ActionTypes.clearAll:
      return {
        ...state,
        filters: new Map(),
        search: undefined,
      };
    case ActionTypes.updateFilterCondition: {
      const filters = new Map(state.filters);
      const filter = filters.get(action.payload.name);

      if (filter) {
        if (isDateFilterName(action.payload.name)) {
          filters.set(action.payload.name, {
            ...filter,
            condition: action.payload.condition,
          } as DateFrontFilterValue);
        } else {
          filters.set(action.payload.name, {
            ...filter,
            condition: action.payload.condition,
          } as StringFrontFilterValue);
        }
      }

      return {
        ...state,
        filters,
      };
    }
    case ActionTypes.clearSearch:
      return {
        ...state,
        search: undefined,
      };
    case ActionTypes.updateSearch:
      return {
        ...state,
        search: action.payload.search,
      };
    default:
      return state;
  }
}

interface ReturnRequestsFiltersProviderProps extends PropsWithChildren {
  onFiltersChange: (
    filters: ReturnRequestsFiltersContextValue["filters"],
  ) => void;
  initialState: State;
}

export const ReturnRequestsFiltersProvider = ({
  children,
  onFiltersChange,
  initialState,
}: ReturnRequestsFiltersProviderProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const { data: shop } = useCurrentShopSuspense();
  const filtersDefinition = useFiltersDefinition({ shop });
  const [state, dispatch] = useReducer(filtersReducer, initialState);
  const [view, setView] = useState<ShopViewDto | null>(null);
  const [page, _setPage] = useState(1);

  const [quickFilter, setQuickFilter] = useState<QuickFilter | null>(null);

  const setPage = (page: number) => {
    _setPage(page);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const clearAll = useCallback(() => {
    dispatch({ type: ActionTypes.clearAll, payload: undefined });
  }, []);

  const addFilter = useCallback((payload: AddFilterPayload) => {
    dispatch({ type: ActionTypes.addFilter, payload });
  }, []);

  const removeFilter = useCallback((payload: RemoveFilterPayload) => {
    dispatch({ type: ActionTypes.removeFilter, payload });
  }, []);

  const clearFilter = useCallback((payload: ClearFilterPayload) => {
    dispatch({ type: ActionTypes.clearFilter, payload });
  }, []);

  const updateFilterCondition = useCallback(
    (payload: UpdateFilterConditionPayload) => {
      dispatch({ type: ActionTypes.updateFilterCondition, payload });
    },
    [],
  );

  const updateSearch = useCallback((payload: UpdateSearchPayload) => {
    dispatch({ type: ActionTypes.updateSearch, payload });
  }, []);

  const clearSearch = useCallback(() => {
    dispatch({ type: ActionTypes.clearSearch, payload: undefined });
  }, []);

  const isAnyFilterApplied = state.filters.size > 0;

  useEffect(() => {
    onFiltersChange(state.filters);
  }, [onFiltersChange, state]);

  return (
    <ReturnRequestsFiltersContext.Provider
      value={{
        quickFilter,
        setQuickFilter,
        filters: state.filters,
        search: state.search,
        sorting,
        setSorting,
        addFilter,
        removeFilter,
        clearFilter,
        filtersDefinition,
        isAnyFilterApplied,
        clearAll,
        updateFilterCondition,
        updateSearch,
        clearSearch,
        view,
        setView,
        page,
        setPage,
      }}
    >
      {children}
    </ReturnRequestsFiltersContext.Provider>
  );
};
